// GAVION gavion-context: the default « Contexte Gavion » — the company and product context distilled from gavion.ai
// (read 2026-10-20) with the owner's rulings applied (setup « en seulement quelques jours », no « 99 % » nor « 12 h+ »,
// the AI-disclosure claim allowed, Loi 25 wording, prices as on the site, no open-items section) — and its trusted facts.
// The SQL seed (supabase/schemas/73_gavion_company_context.sql) carries the same text and facts, pinned equal by
// scripts/gavion/opsParity.test.ts. Data only: the readers are in companyContext.ts.
export type CompanyFact = { text: string; source: string };

export const DEFAULT_COMPANY_CONTEXT = `# Contexte Gavion

Fiche tirée du site gavion.ai (lu le 2026-10-20) et validée par la direction de Gavion. La source est indiquée à la fin de chaque section. Règles de base pour les courriels et les appels : ne jamais présenter une fonction « Bientôt » comme disponible, ne jamais inventer de client, de témoignage ou de statistique, citer les prix en dollars canadiens, avant taxes, et dire seulement que la mise en place se fait « en seulement quelques jours » (aucun autre délai).

## 1. Qui est Gavion / Gala

- **Gavion inc.** est une entreprise montréalaise d'IA vocale. Elle développe **Gala**, « une réceptionniste IA bilingue et une assistante administrative pour les entreprises de services et de construction au Canada ».
- Fondée en 2026 par Antoine Gagnon (chef de la direction), Vincent Gagnon (directeur technique) et Martin Gagnon (directeur des opérations).
- Gala existe en deux rôles : **Gala la réceptionniste** (l'accueil, pendant l'appel) et **Gala l'assistante administrative** (le travail de bureau après l'appel). L'assistante administrative **inclut la réceptionniste complète**.
- Clientèle visée : « les PME de services et de construction du Québec : plomberie, CVAC, électricité, toiture, paysagement, déneigement, mécanique auto, extermination, ménage, peinture, revêtement de sol, salons et plus. »

Phrases-clés exactes du site :
- « Gala répond. Chaque appel. Envoie chaque soumission. Fait chaque suivi. »
- « Réceptionniste et assistante administrative IA pour entrepreneurs et entreprises de services. Vous restez sur le terrain. »
- « Elle répond à chaque appel 24/7 en français québécois et en anglais, qualifie les prospects, prend les rendez-vous en direct dans votre agenda […] Conçue au Québec, Canada, avec vos données hébergées au Canada. »
- « Pas de menu. Pas de musique d'attente. Pas de « faites le 1 pour les ventes ». »
- « Un seul numéro, deux langues, aucun supplément. »
- « Mieux vaut un transfert qu'une réponse inventée. »
- « Un appel manqué, c'est un client qui appelle la prochaine compagnie sur sa liste. »
- Sur le remplacement du personnel : « Ce n'est pas l'argument que nous faisons. » Le gain se mesure « en soirées récupérées et en contrats qui ne partent plus chez le concurrent, pas en postes coupés ».

Source : https://gavion.ai/fr, https://gavion.ai/fr/about, https://gavion.ai/fr/faq, https://gavion.ai/fr/receptionniste-ia

## 2. Ce que Gala fait aujourd'hui vs « Bientôt »

Le site distingue clairement « En ligne » et « À venir » : « Tout ce qui est marqué À venir ne tourne pas, et reste écrit au futur tant que ce n'est pas livré. »

### Disponible aujourd'hui — Réceptionniste (dans chaque forfait)
- Réponse 24/7 par appel, texto et courriel, soirs, fins de semaine et jours fériés.
- Plusieurs appels à la fois : aucune ligne occupée, aucune mise en attente.
- Français québécois et anglais, détectés automatiquement; bascule en cours d'appel sans transfert. Le site indique 44 langues prises en charge.
- Qualification des prospects avec vos questions; tri des urgences selon vos règles.
- Prise de rendez-vous en direct dans Google Agenda ou Outlook (Microsoft 365), pendant l'appel.
- Transfert intelligent : les urgences vont à votre ligne de garde, avec texto à l'équipe; si personne ne répond, message détaillé et rappel prioritaire.
- Filtrage des pourriels, de la sollicitation et des robots d'appel, avec un sommaire quotidien de ce qui a été bloqué.
- Confirmations et rappels par texto et courriel; réponses aux textos et courriels entrants.
- Reconnaissance des clients récurrents à partir de votre CRM (gestion de la relation client); résumé écrit de chaque appel dans le tableau de bord.
- Appels sortants (inclus dans tous les forfaits) : rappel avant la visite, appel pour une information manquante, relance des soumissions, réactivation des anciens clients, dans les heures que vous fixez.
- **Relance des factures impayées** : en ligne, incluse dans chaque forfait Réceptionniste, sans frais supplémentaires.
- Demande d'avis Google envoyée le lendemain de la signature.
- Tableau de bord : appels, transcriptions, réservations, usage, analytique; accessible sur ordinateur ou téléphone.
- Garde-fous : pas de prix ferme au téléphone, pas de disponibilité non vérifiée, rien qui ne soit dans votre base de connaissances.

### Disponible aujourd'hui — Assistante administrative (en plus)
- Devis par photo : le client envoie une photo ou une adresse; vision IA et mesure satellite (pelouses, entrées, toits); devis préliminaire « en quelques minutes », dans vos fourchettes de prix approuvées, avec tolérance indiquée et lien de signature électronique. Signalé pour visite si la photo manque de données. Le prix ferme reste confirmé par votre équipe.
- CRM 360° : chaque appel, message, intervention et paiement enrichit la fiche client.
- Documents et registres : contrats, bons de travail, rapports photo avant-après, certificats de fin de chantier; registre des consentements, journal d'audit, export et droit à l'effacement.
- Analytique : chiffre d'affaires par technicien, taux de conversion par source, facture moyenne par service, prédictions saisonnières.
- Gestion des courriels entrants (tri, réponses standards, acheminement).

### Bientôt (pas encore en ligne — ne jamais vendre comme disponible)
- **Planification et répartition des équipes** (assigner le bon technicien, réajuster la journée).
- **Synchro QuickBooks** (factures, paiements, comptes à recevoir, codes de taxe).
- **Acomptes et paiements Stripe** (acompte à la réservation, paiement final à la fin des travaux).

Formulation sûre : « La relance des factures impayées est en ligne aujourd'hui. QuickBooks, Stripe et la répartition arrivent bientôt. »

Source : https://gavion.ai/fr/receptionniste-ia, https://gavion.ai/fr/assistante-administrative-ia, https://gavion.ai/fr/integrations, https://gavion.ai/fr/faq, https://gavion.ai/fr/use-cases, https://gavion.ai/fr/appels-sortants, https://gavion.ai/fr/relances-comptables, https://gavion.ai/fr/soumission-automatisee

## 3. Forfaits et prix (CAD, par mois, avant TPS et TVQ)

« Forfaits mensuels fixes en dollars canadiens. Chaque prix ci-dessous est celui que vous payez aujourd'hui. »

### Gala la réceptionniste
| Forfait | Prix | Minutes | Usagers | Agents vocaux | Minute au-delà |
|---|---|---|---|---|---|
| Essentielle | 129 $/mois | 250 | 3 | 1 | 0,37 $ |
| Avancé | 249 $/mois | 500 | 5 | 2 | 0,35 $ |
| Entreprise | 439 $/mois | 1 000 | 7 | 3 | 0,33 $ |

Essentielle : « Pour les travailleurs autonomes et les petites équipes. » Avancé : « Pour les équipes en croissance au volume d'appels régulier. » Entreprise : « Pour les opérations occupées à plusieurs équipes. »
Extras : usager additionnel 22 $/mois · agent vocal additionnel 10 $/mois. Rabais volume disponibles pour les usagers additionnels.

### Gala l'assistante administrative (inclut la réceptionniste complète)
| Forfait | Prix | Minutes | Usagers | Devis | Minute au-delà |
|---|---|---|---|---|---|
| Essentielle | 279 $/mois | 250 | 3 | 5/mois | 0,42 $ |
| Avancé | 429 $/mois | 500 | 5 | 15/mois | 0,40 $ |
| Entreprise | 659 $/mois | 1 000 | 7 | illimités | 0,38 $ |

Extras : usager additionnel 39 $/mois · agent vocal additionnel 10 $/mois · devis additionnel dès 8 $. Rabais volume disponibles pour les usagers additionnels.

### Conditions
- **0 $ d'installation** sur tous les forfaits. L'accompagnement à la mise en route est offert en option (« à la carte »).
- **Facturation annuelle : 15 % de rabais de plus**, payée en un seul versement au début de la période.
- Facturation mensuelle, sans contrat à long terme, annulable en tout temps, aucuns frais d'annulation. Le forfait reste actif jusqu'à la fin de la période de facturation en cours.
- Changement de forfait (vers le haut ou le bas) en tout temps; s'applique au prochain cycle de facturation.
- Minutes excédentaires facturées à la minute supérieure.
- Plus de 1 000 minutes par mois : configuration adaptée sur demande.
- Un **usager** = une personne avec un accès nommé à la plateforme. Un **agent vocal** = une personnalité téléphonique distincte, avec son propre numéro et son propre script (ex. deux bannières). Les clients qui appellent ne sont pas des usagers.
- Tous les forfaits Réceptionniste ont les mêmes fonctions (urgences 24/7 et transferts compris); bilinguisme inclus.
- Aucune garantie de remboursement ni période d'essai n'est publiée. On peut entendre Gala sur la ligne de démonstration (section 11) ou demander qu'elle vous rappelle.

Source : https://gavion.ai/fr/pricing, https://gavion.ai/pricing, https://gavion.ai/, https://gavion.ai/fr/faq, https://gavion.ai/fr/cout-receptionniste-ia, https://gavion.ai/fr/plomberie

## 4. Intégrations

**En ligne aujourd'hui :** Google Agenda; Microsoft Outlook (Microsoft 365); confirmations et rappels par texto et courriel; réponses aux textos et courriels entrants; transfert d'appel vers votre ligne; appels sortants; votre numéro d'affaires actuel (renvoi d'appel, aucun matériel); relance des factures impayées; demandes d'avis Google; vos outils par API ou webhook (« on peut généralement le connecter »). Votre liste de clients se connecte par votre CRM ou s'importe.

**À venir :** QuickBooks; acomptes Stripe; planification et répartition.

Conseil du site à tout acheteur : « Nommez vos outils avant de signer. Demandez à voir l'intégration en fonctionnement, pas une case cochée dans un tableau comparatif. » Pour un logiciel de gestion terrain précis, on confirme avant la signature.

Source : https://gavion.ai/fr/integrations, https://gavion.ai/fr/comment-ca-marche, https://gavion.ai/fr/faq

## 5. Sécurité et données

- Données de compte, registres d'appels et transcriptions stockés **au Canada** (région ca-central-1). Serveur applicatif (agent vocal et tableau de bord) **au Québec, à Beauharnois**.
- Audio des appels en direct : **rétention zéro** chez le fournisseur vocal; il ne conserve rien après l'appel.
- Enregistrements (si vous les activez) : stockés au Canada, **supprimés après 90 jours**.
- Chiffrement : TLS 1.3 en transit, AES-256 au repos. Sauvegardes sur infrastructure canadienne.
- Vos données ne sont **jamais vendues**, jamais utilisées pour entraîner des modèles tiers, jamais partagées à des fins de marketing.
- Le client est propriétaire de ses données; accès par rôle (un technicien voit ses travaux, le propriétaire voit tout).
- Demande de copie ou de suppression : privacy@gavion.ai, réponse en 30 jours.
- Enregistrement des appels : le site recommande une phrase d'accueil qui annonce l'enregistrement, sa fin et demande l'accord (alinéa 184(2)a) du Code criminel).
- Loi 25 : dire seulement que les données sont hébergées au Canada et que la politique de confidentialité est rédigée pour la Loi 25 du Québec et la LPRPDE. Le site précise : « Rien ici n'est une certification. C'est simplement où sont les fichiers. » Ne jamais affirmer de certification, ni présenter Gavion comme approuvée ou certifiée au regard de la Loi 25.
- Santé : le site ne présente pas Gala comme adaptée aux régimes particuliers de ce secteur.

Source : https://gavion.ai/fr/securite, https://gavion.ai/fr/faq, https://gavion.ai/privacy

## 6. Comment ça marche / mise en place

La mise en place se fait **en seulement quelques jours**, avec une configuration guidée :
1. Créez votre réceptionniste (une première « mini Gala »).
2. Montrez-lui votre entreprise : services, prix, territoires, règles d'urgence, ce qu'elle a le droit de dire; branchez l'agenda et la liste de clients.
3. Branchez votre téléphone : renvoi de votre numéro actuel. Les clients appellent le même numéro.
4. Testez et mettez en service.
5. De la mini Gala à la grande Gala : ajoutez services, règles et intégrations.

- Rien à installer, aucun matériel. Gala est hébergée : rien ne dépend de la connexion Internet du bureau.
- Ce qui fixe le rythme, c'est la taille de l'entreprise (nombre de services, d'employés, de règles). Désignez la personne qui remplit et valide la base de connaissances.
- Vous décidez avant le lancement : accueil et ton, questions de qualification, limites (pas de prix ferme, pas d'engagement de délai, pas de conseil technique hors périmètre), ce qui compte comme une urgence.
- Après : écouter les appels du premier mois, puis une revue de 30 minutes chaque trimestre.
- Soutien : équipe bilingue établie au Québec, soutien 24/7.

Source : https://gavion.ai/fr/comment-ca-marche, https://gavion.ai/fr/faq, https://gavion.ai/fr

## 7. Industries et cas d'usage

Le site couvre 36 industries (dont portes de garage et extermination). Pour les métiers visés :

- **Électriciens** — Douleur : une odeur de brûlé au panneau à 23 h tombe sur la boîte vocale et le client appelle l'électricien suivant; après un verglas, tout le monde appelle le même matin; les soumissions de panneau et de borne de recharge attendent derrière les urgences. Résultat visé : tri des pannes, appels de sécurité transférés à l'électricien de garde, visites d'estimation réservées avec confirmation par texto.
- **Plombiers** — Douleur : un tuyau éclate à 2 h et « le premier qui répond prend l'urgence »; le premier gel amène une vague d'appels. Résultat : questions d'urgence posées à chaque appel, transfert au plombier de garde avec adresse et résumé, petits bris réservés dans la prochaine plage libre.
- **CVAC** — Douleur : panne de chauffage à -20, odeur de gaz ou alarme de monoxyde qui ne peut pas attendre la boîte vocale; canicules de juillet. Résultat : tri des pannes, appels de gaz et de monoxyde transférés, entretiens réservés, détails de l'équipement captés.
- **Serruriers** — Douleur : « Chaque minute en boîte vocale, c'est un client qui appelle le prochain Google. » Résultat : file prioritaire pour les verrouillages, routage auto / résidentiel / commercial, explication calme du tarif hors heures, rappel d'avoir une pièce d'identité.
- **Couvreurs** — Douleur : fuites pendant les orages, barrages de glace, vagues d'appels après les grands vents. Résultat : tri des fuites, transfert à l'équipe de garde, détails de réclamation d'assurance captés, inspections réservées.
- **Construction** — Demandes de soumission (pi², échéancier, budget) captées, visites réservées.
- **Déneigement** — Tempête de nuit : tri des sorties bloquées, déneigements ponctuels réservés, appels sortants de renouvellement à l'automne.
- **Aménagement paysager** — Ruée du printemps : estimations réservées pendant l'appel.

Cas d'usage : prise de rendez-vous, réponse hors heures, capture de prospects, filtrage des appels, soumissions automatisées, répartition d'équipe (Bientôt), relance des factures impayées, automatisation du CRM, appels sortants, réponse par texto et courriel, relances et confirmations, communications centralisées.

Source : https://gavion.ai/fr/industries, https://gavion.ai/fr/use-cases, https://gavion.ai/fr/electricien, https://gavion.ai/fr/plomberie, https://gavion.ai/fr/cvac, https://gavion.ai/fr/serrurier, https://gavion.ai/fr/toiture, https://gavion.ai/fr/construction, https://gavion.ai/fr/deneigement, https://gavion.ai/fr/amenagement-paysager

## 8. Résultats et chiffres

**Aucun témoignage client, aucune étude de cas nommée et aucun nombre de clients n'est publié sur le site.** Ne jamais en inventer.

Chiffres affichés par le site (à citer avec leur libellé exact) :
- Devis par photo : « en quelques minutes »; la FAQ dit « en environ 60 secondes ».
- Calculateur de ROI, exemple donné par le site : un plombier à environ 350 $ par contrat, 20 appels manqués par mois, 40 % de conversion → 20 × 40 % × 350 $ = 2 800 $ par mois, moins 129 $ = 2 671 $. À 10 % de conversion : 700 $, soit 571 $ après le forfait. « C'est une estimation, pas une promesse. »
- « La plupart des métiers se situent entre 30 % et 60 % » de conversion (calculateur).
- Salaire médian d'une réceptionniste au Québec : 20 $/h, environ 3 033 $ par mois à 35 h/semaine (Guichet-Emplois, 2023-2024), contre 129 $/mois pour le forfait d'entrée.

Le message du site sur la rentabilité : « Le seuil de rentabilité se mesure en contrats, pas en mois. […] un seul appel converti en contrat de plus de 129 $ rembourse le mois. » « Mesurez sur trois mois, pas sur trois jours. » Ne jamais garantir un résultat chiffré.

Source : https://gavion.ai/fr/receptionniste-ia, https://gavion.ai/fr/assistante-administrative-ia, https://gavion.ai/fr/faq, https://gavion.ai/fr/calculateur-roi, https://gavion.ai/fr/receptionniste-ia-quebecoise, https://gavion.ai/fr/salaire-receptionniste-quebec

## 9. Objections fréquentes et réponses

- **« Mes clients n'aiment pas parler à un robot. »** Voix naturelle, conversationnelle, en temps réel; pas de menu à touches. Le site : « La plupart des appelants ne devinent pas que Gala est une IA. » (argument permis). Ne jamais dire que Gala cache être une IA quand on le lui demande. L'alternative actuelle, c'est souvent la boîte vocale.
- **« Et si elle ne sait pas la réponse? »** « Elle n'invente jamais. » Elle reformule une fois, puis transfère à un humain ou consigne un message détaillé avec rappel prioritaire.
- **« Elle va donner des prix n'importe comment. »** Pas de prix ferme au téléphone; seulement les montants que vous approuvez (ex. frais de déplacement). Les devis restent dans vos fourchettes.
- **« Je ne suis pas technique. »** Rien à installer, aucun matériel, étapes guidées et équipe de soutien au besoin.
- **« Ça prend combien de temps à mettre en place? »** En seulement quelques jours.
- **« Je dois changer de numéro? »** Non, simple renvoi d'appel.
- **« Je suis trop petit. »** Les micro-entreprises sont « précisément le profil où l'écart est le plus grand, parce que personne d'autre ne répond ». Faites le calcul : valeur d'un contrat × appels manqués; si ça dépasse 129 $, un seul contrat récupéré paie le mois.
- **« Je ne veux pas de contrat. »** Mois par mois, annulable en tout temps, aucuns frais d'annulation. Installation : 0 $. La facturation annuelle donne 15 % de rabais, payée d'avance.
- **« Ça remplace ma réceptionniste? »** Non : Gala prend ce que personne ne fait (appels du soir, relances, soumissions qui attendent).
- **« La boîte vocale me suffit. »** La plupart des clients qui ont un contrat à donner ne laissent pas de message; ils essaient le numéro suivant.
- **« Mes données? »** Hébergées au Canada, jamais vendues, jamais utilisées pour l'entraînement.
- **« ChatGPT peut le faire. »** Un assistant conversationnel « ne répond pas au téléphone à 19 h, ne connaît pas vos prix, n'accède pas à votre agenda ».
- **« Puis-je l'essayer? »** Oui : appelez la ligne de démonstration ou demandez que Gala vous rappelle.

Source : https://gavion.ai/fr/faq, https://gavion.ai/fr/receptionniste-ia, https://gavion.ai/fr/calculateur-roi, https://gavion.ai/fr/appeler-gala, https://gavion.ai/fr/plomberie

## 10. Différenciation vs concurrents

Méthode du site : « Des faits lus sur la page publique de chaque fournisseur, à une date indiquée […] Rien de déduit, rien d'arrondi en notre faveur. » Gavion n'est affiliée à aucun. Rester factuel, ne jamais dénigrer; chaque page dit aussi pour qui le concurrent est un bon choix.

Ce qui distingue Gala selon le site :
- **Français québécois et anglais** sur le même numéro, sans supplément. « Bilingue » veut souvent dire anglais et espagnol chez les services américains.
- **Prix en dollars canadiens**, avec minutes incluses et tarif publié pour la minute suivante.
- **Données au Canada**, serveur au Québec.
- **0 $ d'installation**.
- **Prise de rendez-vous en direct dès le forfait d'entrée**, sans frais par réservation.
- Réceptionniste et travail de bureau chez un seul fournisseur.

Exemples publiés (écarts relevés par le site; ne jamais citer les prix des concurrents) :
- **Ruby** (États-Unis, réceptionnistes humaines) : prix en dollars américains, et beaucoup moins de minutes incluses que le forfait d'entrée de Gala.
- **Smith.ai** (États-Unis) : prix en dollars américains, forfaits limités en nombre d'appels, prise de rendez-vous facturée à la réservation sur ses forfaits humains; le français n'est pas mentionné.
- **Posh** (Canada, humains) : un tarif à la minute en plus du forfait mensuel; « 100 minutes chez Posh coûtent déjà plus que le forfait d'entrée de Gala ».
- Concurrents québécois et canadiens (Voxify, Agent IA Vocal, Nexia, Noémie, Aria, Captia, Allesi…) : écarts relevés sur la devise, les frais d'installation, les minutes ou le tarif au-delà non publiés, l'anglais ou la prise de rendez-vous au forfait d'entrée.
- Quand un concurrent convient mieux, le site le dit (ex. Fulltime.ai pour ceux déjà sur Jobber, Fergus ou Progression; Smith.ai pour une clientèle américaine voulant des humains).

Source : https://gavion.ai/fr/comparer, https://gavion.ai/fr/comparer/ruby, https://gavion.ai/fr/comparer/smith-ai, https://gavion.ai/fr/comparer/posh, https://gavion.ai/fr/comparer/fulltime-ai, https://gavion.ai/fr/cout-receptionniste-ia

## 11. Coordonnées

- Gavion inc., 2901, rue Rachel Est, Montréal (Québec) H1W 4A4, Canada.
- Courriel : contact@gavion.ai · Confidentialité : privacy@gavion.ai
- Téléphone : +1 (514) 534-1298 (c'est aussi la ligne où Gala répond en direct) · Sans frais : +1 (855) 505-0607
- Service partout au Canada, en français et en anglais.
- Site : gavion.ai (tarifs : gavion.ai/fr/pricing)

Source : https://gavion.ai/fr/contact, https://gavion.ai/fr/appeler-gala`;

export const DEFAULT_COMPANY_FACTS: readonly CompanyFact[] = [
  {
    text: "Gala la réceptionniste, forfait Essentielle : 129 $/mois CAD, 250 minutes/mois, 3 usagers, 1 agent vocal, 0,37 $/min au-delà du forfait",
    source: "https://gavion.ai/fr/pricing",
  },
  {
    text: "Gala la réceptionniste, forfait Avancé : 249 $/mois CAD, 500 minutes/mois, 5 usagers, 2 agents vocaux, 0,35 $/min au-delà du forfait",
    source: "https://gavion.ai/fr/pricing",
  },
  {
    text: "Gala la réceptionniste, forfait Entreprise : 439 $/mois CAD, 1 000 minutes/mois, 7 usagers, 3 agents vocaux, 0,33 $/min au-delà du forfait",
    source: "https://gavion.ai/fr/pricing",
  },
  {
    text: "Réceptionniste IA à partir de 129 $/mois CAD",
    source: "https://gavion.ai/fr",
  },
  {
    text: "Assistante administrative (inclut la réceptionniste complète) à partir de 279 $/mois CAD",
    source: "https://gavion.ai/fr",
  },
  {
    text: "Gala l'assistante administrative, forfait Essentielle : 279 $/mois CAD, 250 minutes/mois, 3 usagers, 5 devis/mois, 0,42 $/min au-delà du forfait",
    source: "https://gavion.ai/fr/pricing",
  },
  {
    text: "Gala l'assistante administrative, forfait Avancé : 429 $/mois CAD, 500 minutes/mois, 5 usagers, 15 devis/mois, 0,40 $/min au-delà du forfait",
    source: "https://gavion.ai/fr/pricing",
  },
  {
    text: "Gala l'assistante administrative, forfait Entreprise : 659 $/mois CAD, 1 000 minutes/mois, 7 usagers, devis illimités, 0,38 $/min au-delà du forfait",
    source: "https://gavion.ai/fr/pricing",
  },
  {
    text: "Usager additionnel : 22 $/mois (Réceptionniste)",
    source: "https://gavion.ai/fr/pricing",
  },
  {
    text: "Usager additionnel : 39 $/mois (Assistante administrative)",
    source: "https://gavion.ai/fr/pricing",
  },
  {
    text: "Agent vocal additionnel : 10 $/mois",
    source: "https://gavion.ai/fr/pricing",
  },
  {
    text: "Devis additionnel dès 8 $",
    source: "https://gavion.ai/fr/pricing",
  },
  {
    text: "Rabais volume disponibles pour les usagers additionnels",
    source: "https://gavion.ai/fr/pricing",
  },
  {
    text: "0 $ d'installation sur tous les forfaits",
    source: "https://gavion.ai/fr/pricing",
  },
  {
    text: "Facturation annuelle : 15 % de rabais, payée en un seul versement au début de la période",
    source: "https://gavion.ai/fr/pricing",
  },
  {
    text: "Facturation mensuelle, sans contrat à long terme, annulable en tout temps",
    source: "https://gavion.ai/fr/pricing",
  },
  {
    text: "Mois par mois, annulable en tout temps, aucuns frais d'annulation",
    source: "https://gavion.ai/fr",
  },
  {
    text: "Le forfait reste actif jusqu'à la fin de la période de facturation en cours après l'annulation",
    source: "https://gavion.ai/",
  },
  {
    text: "Changement de forfait en tout temps, appliqué au prochain cycle de facturation",
    source: "https://gavion.ai/fr/faq",
  },
  {
    text: "Minutes excédentaires de 0,33 $ à 0,42 $ selon le forfait, facturées à la minute supérieure",
    source: "https://gavion.ai/fr/faq",
  },
  {
    text: "Plus de 1 000 minutes par mois : configuration adaptée sur demande",
    source: "https://gavion.ai/fr/pricing",
  },
  {
    text: "Les prix publiés sont avant TPS et TVQ",
    source: "https://gavion.ai/fr/cout-receptionniste-ia",
  },
  {
    text: "L'accompagnement à la mise en route est offert en option (à la carte)",
    source: "https://gavion.ai/fr/faq",
  },
  {
    text: "Bilinguisme inclus dans tous les forfaits, aucun supplément",
    source: "https://gavion.ai/fr/plomberie",
  },
  {
    text: "Prise de rendez-vous dans Google ou Outlook sur tous les forfaits, sans frais par réservation",
    source: "https://gavion.ai/fr/cout-receptionniste-ia",
  },
  {
    text: "Appels sortants inclus dans tous les forfaits, dès le forfait d'entrée, sans frais supplémentaires",
    source: "https://gavion.ai/fr/appels-sortants",
  },
  {
    text: "Relance des factures impayées en ligne, incluse dans chaque forfait Réceptionniste sans frais supplémentaires",
    source: "https://gavion.ai/fr/relances-comptables",
  },
  {
    text: "Réponse 24/7 par appel, texto et courriel",
    source: "https://gavion.ai/fr/receptionniste-ia",
  },
  {
    text: "Répond en français québécois et en anglais, 24/7",
    source: "https://gavion.ai/fr",
  },
  {
    text: "Gala prend en charge 44 langues, avec le français et l'anglais détectés automatiquement sur tous les forfaits",
    source: "https://gavion.ai/fr/faq",
  },
  {
    text: "Gala répond à plusieurs appels en même temps, aucune ligne occupée",
    source: "https://gavion.ai/fr/faq",
  },
  {
    text: "Filtre les pourriels et robots d'appel avec un sommaire quotidien de ce qui a été bloqué",
    source: "https://gavion.ai/fr",
  },
  {
    text: "Demande d'avis Google envoyée le lendemain de la signature",
    source: "https://gavion.ai/fr/integrations",
  },
  {
    text: "Devis préliminaire par photo ou adresse en quelques minutes (vision IA et mesure satellite)",
    source: "https://gavion.ai/fr/assistante-administrative-ia",
  },
  {
    text: "Soumission à partir d'une photo ou d'une image satellite en environ 60 secondes",
    source: "https://gavion.ai/fr/faq",
  },
  {
    text: "Mise en place en seulement quelques jours",
    source: "Décision de la direction Gavion (2026-10-20)",
  },
  {
    text: "Revue de 30 minutes de la base de connaissances chaque trimestre",
    source: "https://gavion.ai/fr/comment-ca-marche",
  },
  {
    text: "Soutien 24/7, équipe bilingue établie au Québec",
    source: "https://gavion.ai/fr/faq",
  },
  {
    text: "Données de compte, registres d'appels et transcriptions stockés au Canada (ca-central-1)",
    source: "https://gavion.ai/fr/securite",
  },
  {
    text: "Serveur applicatif au Québec, à Beauharnois",
    source: "https://gavion.ai/fr/securite",
  },
  {
    text: "Audio des appels en direct en rétention zéro chez le fournisseur vocal",
    source: "https://gavion.ai/fr/securite",
  },
  {
    text: "Enregistrements activés stockés au Canada et supprimés après 90 jours",
    source: "https://gavion.ai/fr/securite",
  },
  {
    text: "Chiffrement TLS 1.3 en transit et AES-256 au repos",
    source: "https://gavion.ai/fr/securite",
  },
  {
    text: "Données jamais vendues, jamais utilisées pour entraîner des modèles tiers",
    source: "https://gavion.ai/fr/securite",
  },
  {
    text: "Réponse aux demandes de copie ou de suppression en 30 jours (privacy@gavion.ai)",
    source: "https://gavion.ai/fr/securite",
  },
  {
    text: "Exemple du calculateur : 20 appels manqués × 40 % × 350 $ = 2 800 $ par mois, soit 2 671 $ après le forfait de 129 $",
    source: "https://gavion.ai/fr/calculateur-roi",
  },
  {
    text: "À 10 % de conversion, le même exemple récupère 700 $, soit 571 $ après le forfait",
    source: "https://gavion.ai/fr/calculateur-roi",
  },
  {
    text: "La plupart des métiers se situent entre 30 % et 60 % de conversion",
    source: "https://gavion.ai/fr/calculateur-roi",
  },
  {
    text: "Sur le forfait Essentielle à 129 $, un seul appel converti en contrat de plus de 129 $ rembourse le mois",
    source: "https://gavion.ai/fr/faq",
  },
  {
    text: "Salaire médian d'une réceptionniste au Québec : 20 $/h, environ 3 033 $ par mois (35 h/semaine, Guichet-Emplois 2023-2024)",
    source: "https://gavion.ai/fr/salaire-receptionniste-quebec",
  },
  {
    text: "Le site couvre 36 industries",
    source: "https://gavion.ai/fr/industries",
  },
  {
    text: "Gavion inc., 2901, rue Rachel Est, Montréal (Québec) H1W 4A4",
    source: "https://gavion.ai/fr/contact",
  },
  {
    text: "Téléphone : +1 (514) 534-1298; sans frais : +1 (855) 505-0607",
    source: "https://gavion.ai/fr/contact",
  },
  {
    text: "Courriel : contact@gavion.ai",
    source: "https://gavion.ai/fr/contact",
  },
  {
    text: "Gavion fondée en 2026 à Montréal",
    source: "https://gavion.ai/fr/about",
  },
];
